import { useCallback, useEffect, useState } from "react"
import { say } from "../../../helpers/speech"
import { useMouse } from "./useMouseWordsearch"

const makeEmptyMatrix = (size) => {
  return Array(size)
    .fill()
    .map((row, rix) =>
      Array(size)
        .fill()
        .map((col, cix) => ({
          x: cix,
          y: rix,
          // custom del juego
          char: null,
          done: false,
          color: null,
          overloaded: false,
        }))
    )
}

const makeNDump = (data) => {
  if (!data) return makeEmptyMatrix(0)

  let size = data.puzzle.length,
    m = makeEmptyMatrix(size)

  data.puzzle.forEach((row, rix) => {
    row.forEach((col, cix) => {
      m[rix][cix].char = col.toUpperCase()
    })
  })

  return m
}

const oneify = (x) => (x === 0 ? 0 : Math.abs(x) / x)

export const path = (selection) => {
  const dx = selection.to.x - selection.from.x,
    dy = selection.to.y - selection.from.y,
    m = dy / dx,
    isAcceptedDir = [0, 1, Infinity, NaN].includes(Math.abs(m))

  if (!isAcceptedDir) return []

  const grad = [dx, dy].map(oneify),
    dist = Math.max(...[dx, dy].map(Math.abs))

  return Array(dist + 1)
    .fill()
    .map((_, i) => ({
      x: selection.from.x + i * grad[0],
      y: selection.from.y + i * grad[1],
    }))
}

const makeNDumpNHighlight = (data, selections, colors, dones) => {
  let m = makeNDump(data)

  selections.forEach((sel, ix) => {
    path(sel).forEach((cell) => {
      m[cell.y][cell.x].overloaded = m[cell.y][cell.x].done
      m[cell.y][cell.x].done = true
      m[cell.y][cell.x].word = dones[ix]
      m[cell.y][cell.x].color = colors[ix]
    })
  })

  return m
}

export const useWordsearchCells = (data, winCallback) => {
  let [matrix, setMatrix] = useState(() => makeEmptyMatrix(0))
  let [progress, setProgress] = useState({
    total: 0,
    ratio: 0,
    dones: [],
    selections: [],
    colors: [],
  })

  useEffect(() => {
    setMatrix(() => makeNDump(data))
  }, [data])

  const handleSelection = useCallback(
    (selection) => {
      const selected = path(selection),
        word = selected.map((cell) => matrix[cell.y][cell.x].char).join(""),
        iword = word.split("").reverse().join(""),
        ok = data.clues.some((clue) => clue.definicion.toUpperCase() === word),
        iok = data.clues.some(
          (clue) => clue.definicion.toUpperCase() === iword
        ),
        alreadyDone = progress.dones.includes(word),
        ialreadyDone = progress.dones.includes(iword)

      if (!(ok || iok) || alreadyDone || ialreadyDone) return

      const fword = ok ? word : iword

      say(fword)

      const n = data.clues.filter(
        (clue) => clue.definicion.toUpperCase() === fword
      ).length

      // const random_color = Math.floor(Math.random() * 6) + 1
      const last_color = progress.colors.length
          ? progress.colors.slice(-1)[0]
          : 0,
        new_color = (last_color % 6) + 1

      const newProgress = {
        total: progress.total + n,
        ratio: (progress.total + n) / data.clues.length,
        dones: progress.dones.concat(fword),
        selections: progress.selections.concat(selection),
        colors: progress.colors.concat(new_color),
      }

      setProgress(newProgress)

      setMatrix(() =>
        makeNDumpNHighlight(
          data,
          newProgress.selections,
          newProgress.colors,
          newProgress.dones
        )
      )

      if (newProgress.total === data.clues.length) winCallback()
    },
    [matrix, data, progress, setProgress, winCallback]
  )

  const [tracking, selection] = useMouse(handleSelection)

  return {
    matrix,
    progress,
    tracking,
    selection,
  }
}
